import React, { useState } from 'react';
import { Pencil, Trash2, Plus, Check, X } from 'lucide-react';
import Loader from 'src/shared/components/loader/Loader';
import Alert from 'src/shared/components/ui/alert';
import type { PolygonTag, PolygonTags } from 'src/shared/types/graph';
import { PrimaryButton, SecondaryButton, TertiaryButton } from 'src/shared/components/button/Button';
import Input from 'src/shared/components/input/Input';
import { ControlledConfirmDialog } from 'src/shared/components/confirm/ControlledConfirmDialog';
import {
    useGetSitesWithTagsQuery,
    useAddPolygonLevelTagMutation,
    useDeletePolygonLevelTagMutation,
    useRenamePolygonMutation,
} from './state/api/polygonManagementGraphSlice';
import { validatePolygonName } from './validatePolygonName';

type EditState = {
    assetName: string;
    originalTagName: string;
    name: string;
    value: string;
};

type RenameState = {
    assetName: string;
    value: string;
};

// ── Inline edit/add row ──────────────────────────────────────────────────────

type TagEditRowProps = {
    editing: EditState;
    onSave: () => void;
    onCancel: () => void;
    onChange: (patch: Partial<Pick<EditState, 'name' | 'value'>>) => void;
    isSaving: boolean;
    error: string | null;
};

const TagEditRow = ({ editing, onSave, onCancel, onChange, isSaving, error }: TagEditRowProps) => {
    const isNew = editing.originalTagName === '';
    return (
        <div className='flex flex-col gap-1 px-3 py-2 bg-grey-5'>
            <div className='flex items-center gap-2'>
                <div className='w-36'>
                    <Input
                        type='text'
                        placeholder='Name'
                        value={editing.name}
                        onChange={e => onChange({ name: e.target.value })}
                        disabled={!isNew || isSaving}
                        autoFocus={isNew}
                        hasError={Boolean(error)}
                        data-testid='tag-name-input'
                    />
                </div>
                <div className='flex-1'>
                    <Input
                        type='text'
                        placeholder='Value'
                        value={editing.value}
                        onChange={e => onChange({ value: e.target.value })}
                        disabled={isSaving}
                        autoFocus={!isNew}
                        hasError={Boolean(error)}
                        data-testid='tag-value-input'
                    />
                </div>
                <PrimaryButton
                    className='shrink-0'
                    onClick={onSave}
                    disabled={isSaving || editing.value.trim() === '' || editing.name.trim() === ''}
                    aria-label='Save tag'
                    data-testid='save-tag-button'
                >
                    <Check className='size-3.5' />
                </PrimaryButton>
                <TertiaryButton
                    className='shrink-0'
                    onClick={onCancel}
                    disabled={isSaving}
                    aria-label='Cancel'
                    data-testid='cancel-tag-button'
                >
                    <X className='size-3.5' />
                </TertiaryButton>
            </div>
            {error && <p className='text-xs text-red-100'>{error}</p>}
        </div>
    );
};

// ── Inline polygon rename row ────────────────────────────────────────────────

type PolygonNameEditRowProps = {
    value: string;
    onSave: () => void;
    onCancel: () => void;
    onChange: (value: string) => void;
    isSaving: boolean;
    error: string | null;
};

const PolygonNameEditRow = ({ value, onSave, onCancel, onChange, isSaving, error }: PolygonNameEditRowProps) => (
    <div className='flex flex-col gap-1 flex-1'>
        <div className='flex items-center gap-2'>
            <div className='flex-1'>
                <Input
                    type='text'
                    placeholder='Polygon name'
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    disabled={isSaving}
                    autoFocus
                    hasError={Boolean(error)}
                    data-testid='polygon-name-input'
                />
            </div>
            <PrimaryButton
                className='shrink-0'
                onClick={onSave}
                disabled={isSaving || value.trim() === ''}
                aria-label='Save polygon name'
                data-testid='save-polygon-name-button'
            >
                <Check className='size-3.5' />
            </PrimaryButton>
            <TertiaryButton
                className='shrink-0'
                onClick={onCancel}
                disabled={isSaving}
                aria-label='Cancel rename'
                data-testid='cancel-polygon-name-button'
            >
                <X className='size-3.5' />
            </TertiaryButton>
        </div>
        {error && <p className='text-xs text-red-100'>{error}</p>}
    </div>
);

// ── Per-site block ───────────────────────────────────────────────────────────

type PolygonManagementBlockProps = {
    site: PolygonTags;
    editing: EditState | null;
    onStartAdd: () => void;
    onStartEdit: (tag: PolygonTag) => void;
    onDelete: (tag: PolygonTag) => void;
    onSave: () => void;
    onCancel: () => void;
    onChange: (patch: Partial<Pick<EditState, 'name' | 'value'>>) => void;
    isSaving: boolean;
    isDeleting: boolean;
    formError: string | null;
    renaming: RenameState | null;
    renameActive: boolean;
    onStartRename: () => void;
    onRenameSave: () => void;
    onRenameCancel: () => void;
    onRenameChange: (value: string) => void;
    isRenameSaving: boolean;
    renameError: string | null;
};

const PolygonManagementBlock = ({
    site,
    editing,
    onStartAdd,
    onStartEdit,
    onDelete,
    onSave,
    onCancel,
    onChange,
    isSaving,
    isDeleting,
    formError,
    renaming,
    renameActive,
    onStartRename,
    onRenameSave,
    onRenameCancel,
    onRenameChange,
    isRenameSaving,
    renameError,
}: PolygonManagementBlockProps) => {
    const { name, tags } = site.properties;
    const isAddingNew = editing !== null && editing.originalTagName === '';
    const tagActionsBlocked = editing !== null || renameActive;

    return (
        <div className='border border-grey-60 rounded-lg overflow-hidden text-sm max-w-[600px]'>
            <div className='flex items-center justify-between px-3 py-2 bg-grey-5 border-b border-grey-20'>
                <div className='flex items-center gap-1.5 flex-1'>
                    {renaming ? (
                        <PolygonNameEditRow
                            value={renaming.value}
                            onSave={onRenameSave}
                            onCancel={onRenameCancel}
                            onChange={onRenameChange}
                            isSaving={isRenameSaving}
                            error={renameError}
                        />
                    ) : (
                        <>
                            <span className='font-medium text-grey-100 my-3 text-lg'>{name}</span>
                            <button
                                className='p-1 rounded hover:bg-grey-60 text-grey-50 hover:text-grey-100 disabled:opacity-40'
                                onClick={onStartRename}
                                disabled={tagActionsBlocked}
                                aria-label={`Rename polygon ${name}`}
                                data-testid={`rename-polygon-${name}`}
                            >
                                <Pencil className='size-3' />
                            </button>
                        </>
                    )}
                </div>
            </div>

            <div className='divide-y divide-grey-10'>
                {tags.length === 0 && !isAddingNew && <p className='px-4 py-2 text-sm text-grey-50'>No tags added for this polygon</p>}

                {(tags.length > 0 || isAddingNew) && (
                    <div className='flex items-center px-3 py-2 gap-2 group hover:bg-grey-5 text-primary'>
                        <span className='w-32 shrink-0 truncate text-primary font-bold'>Label</span>
                        <span className='text-grey-70 flex-1 truncate font-bold'>Value</span>
                        <span className='text-grey-70 flex-1 truncate font-bold text-right'>Actions</span>
                    </div>
                )}
                {tags.map(tag => {
                    if (editing !== null && editing.originalTagName === tag.name) {
                        return (
                            <TagEditRow
                                key={tag.name}
                                editing={editing}
                                onSave={onSave}
                                onCancel={onCancel}
                                onChange={onChange}
                                isSaving={isSaving}
                                error={formError}
                            />
                        );
                    }
                    return (
                        <>
                            <div key={tag.name} className='flex items-center px-3 py-2 gap-2 group hover:bg-grey-20'>
                                <span className='font-medium text-grey-90 w-32 shrink-0 truncate'>{tag.name}</span>
                                <span className='text-grey-70 flex-1 truncate'>{tag.value}</span>
                                <div className='flex items-center gap-0.5 ml-auto'>
                                    <button
                                        className='p-1 rounded hover:bg-grey-60 text-grey-50 hover:text-grey-100 disabled:opacity-40'
                                        onClick={() => onStartEdit(tag)}
                                        disabled={tagActionsBlocked || isDeleting}
                                        aria-label={`Edit tag ${tag.name}`}
                                        data-testid={`edit-tag-${name}-${tag.name}`}
                                    >
                                        <Pencil className='size-3' />
                                    </button>
                                    <button
                                        className='p-1 rounded hover:bg-red-0 text-grey-50 hover:text-red-100 disabled:opacity-40'
                                        onClick={() => onDelete(tag)}
                                        disabled={tagActionsBlocked || isDeleting}
                                        aria-label={`Delete tag ${tag.name}`}
                                        data-testid={`delete-tag-${name}-${tag.name}`}
                                    >
                                        <Trash2 className='size-3' />
                                    </button>
                                </div>
                            </div>
                        </>
                    );
                })}

                {isAddingNew && (
                    <TagEditRow
                        editing={editing!}
                        onSave={onSave}
                        onCancel={onCancel}
                        onChange={onChange}
                        isSaving={isSaving}
                        error={formError}
                    />
                )}
            </div>

            <div className='px-3 py-2 border-t border-grey-20'>
                <SecondaryButton
                    className='flex items-center gap-1 text-xs text-primary hover:text-primary-dark font-medium disabled:opacity-40'
                    onClick={onStartAdd}
                    disabled={tagActionsBlocked}
                    data-testid={`add-tag-${name}`}
                >
                    <Plus className='size-3' />
                    Add New Tag
                </SecondaryButton>
            </div>
        </div>
    );
};

// ── Main component ───────────────────────────────────────────────────────────

type PolygonManagementProps = {
    projectId: string;
};

const PolygonManagement = ({ projectId }: PolygonManagementProps) => {
    const [editing, setEditing] = useState<EditState | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [globalError, setGlobalError] = useState<string | null>(null);
    const [renaming, setRenaming] = useState<RenameState | null>(null);
    const [renameError, setRenameError] = useState<string | null>(null);
    const [pendingRename, setPendingRename] = useState<{ assetName: string; newName: string } | null>(null);

    const { data: sites = [], isLoading } = useGetSitesWithTagsQuery({ projectId });
    const [addPolygonLevelTag, { isLoading: isSaving }] = useAddPolygonLevelTagMutation();
    const [deletePolygonLevelTag, { isLoading: isDeleting }] = useDeletePolygonLevelTagMutation();
    const [renamePolygon, { isLoading: isRenameSaving }] = useRenamePolygonMutation();

    const showSuccess = (msg: string) => {
        setGlobalError(null);
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    const handleStartAdd = (assetName: string) => {
        setEditing({ assetName, originalTagName: '', name: '', value: '' });
        setFormError(null);
    };

    const handleStartEdit = (assetName: string, tag: PolygonTag) => {
        setEditing({ assetName, originalTagName: tag.name, name: tag.name, value: tag.value });
        setFormError(null);
    };

    const handleCancel = () => {
        setEditing(null);
        setFormError(null);
    };

    const handleChange = (patch: Partial<Pick<EditState, 'name' | 'value'>>) => {
        setEditing(prev => (prev ? { ...prev, ...patch } : null));
        setFormError(null);
    };

    const handleSave = async () => {
        if (!editing) return;
        if (!editing.name.trim()) {
            setFormError('Tag name is required.');
            return;
        }
        if (!editing.value.trim()) {
            setFormError('Tag value is required.');
            return;
        }
        const isNew = editing.originalTagName === '';
        if (isNew) {
            const site = sites.find(s => s.properties.name === editing.assetName);
            const isDuplicate = site?.properties.tags.some(t => t.name === editing.name.trim());
            if (isDuplicate) {
                setFormError('A tag with this name already exists.');
                return;
            }
        }
        try {
            await addPolygonLevelTag({
                projectId,
                assetName: editing.assetName,
                tagName: editing.name.trim(),
                tagValue: editing.value.trim(),
            }).unwrap();
            showSuccess(editing.originalTagName ? 'Tag updated.' : 'Tag added.');
            setEditing(null);
        } catch {
            setFormError('Failed to save tag. Please try again.');
        }
    };

    const handleStartRename = (assetName: string) => {
        setRenaming({ assetName, value: assetName });
        setRenameError(null);
    };

    const handleRenameChange = (value: string) => {
        setRenaming(prev => (prev ? { ...prev, value } : null));
        setRenameError(null);
    };

    const handleRenameCancel = () => {
        setRenaming(null);
        setRenameError(null);
    };

    const handleRenameSave = () => {
        if (!renaming) return;
        const validation = validatePolygonName(
            renaming.value,
            renaming.assetName,
            sites.map(site => site.properties.name)
        );
        if (validation.kind === 'noop') {
            handleRenameCancel();
            return;
        }
        if (validation.kind === 'error') {
            setRenameError(validation.message);
            return;
        }
        setPendingRename({ assetName: renaming.assetName, newName: validation.name });
    };

    const handleRenameConfirm = async () => {
        if (!pendingRename) return;
        try {
            await renamePolygon({
                projectId,
                assetName: pendingRename.assetName,
                newName: pendingRename.newName,
            }).unwrap();
            setPendingRename(null);
            setRenaming(null);
            setRenameError(null);
            showSuccess('Polygon renamed.');
        } catch {
            setPendingRename(null);
            setRenameError('Failed to rename polygon. Please try again.');
        }
    };

    const handleDelete = async (assetName: string, tag: PolygonTag) => {
        setGlobalError(null);
        try {
            await deletePolygonLevelTag({ projectId, assetName, tagName: tag.name, tagValue: tag.value }).unwrap();
            showSuccess('Tag deleted.');
        } catch {
            setGlobalError('Failed to delete tag. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <div className='flex justify-center py-6'>
                <Loader />
            </div>
        );
    }

    return (
        <div className='flex flex-col gap-3'>
            {successMessage && <Alert severity='success'>{successMessage}</Alert>}
            {globalError && <Alert severity='error'>{globalError}</Alert>}

            {sites.length === 0 ? (
                <p className='text-sm text-grey-50'>No sites found for this project.</p>
            ) : (
                <div className='flex flex-col gap-2'>
                    <div className='text-md mb-5'>Total polygons: {sites.length}</div>
                    {sites.map(site => (
                        <PolygonManagementBlock
                            key={site.properties.name}
                            site={site}
                            editing={editing?.assetName === site.properties.name ? editing : null}
                            onStartAdd={() => handleStartAdd(site.properties.name)}
                            onStartEdit={tag => handleStartEdit(site.properties.name, tag)}
                            onDelete={tag => handleDelete(site.properties.name, tag)}
                            onSave={handleSave}
                            onCancel={handleCancel}
                            onChange={handleChange}
                            isSaving={isSaving}
                            isDeleting={isDeleting}
                            formError={editing?.assetName === site.properties.name ? formError : null}
                            renaming={renaming?.assetName === site.properties.name ? renaming : null}
                            renameActive={renaming !== null}
                            onStartRename={() => handleStartRename(site.properties.name)}
                            onRenameSave={handleRenameSave}
                            onRenameCancel={handleRenameCancel}
                            onRenameChange={handleRenameChange}
                            isRenameSaving={isRenameSaving}
                            renameError={renaming?.assetName === site.properties.name ? renameError : null}
                        />
                    ))}
                </div>
            )}

            <ControlledConfirmDialog
                open={pendingRename !== null}
                onOpenChange={open => {
                    if (!open) setPendingRename(null);
                }}
                variant='confirm'
                title='Rename polygon?'
                description={
                    pendingRename
                        ? `Rename polygon "${pendingRename.assetName}" to "${pendingRename.newName}"? This name is used across maps, tables and filters.`
                        : undefined
                }
                confirmLabel='Rename'
                onConfirm={handleRenameConfirm}
                isConfirming={isRenameSaving}
            />
        </div>
    );
};

export default PolygonManagement;
